import React, { useState } from 'react';
import { getAdminOrders } from '../services/api';

const EXCHANGE_STYLE = {
  NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};

const STATUS_CONFIG = {
  ORDER_RECEIVED: { label: 'Order Received', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  FILE_GENERATED: { label: 'File Generated', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  ORDER_TRADED:   { label: 'Order Traded',   bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
};

const TODAY = new Date().toISOString().slice(0, 10);

function fmtDT(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function OrderReports() {
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);
  const [toast,    setToast]    = useState(null);
  const [modal,    setModal]    = useState(null);
  const [filters,  setFilters]  = useState({
    ucc: '', exchange: 'ALL', segment: 'ALL',
    status: 'ALL', date_from: TODAY, date_to: TODAY
  });

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };
  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const handleSearch = async () => {
    setLoading(true); setSearched(true);
    try {
      const params = {};
      if (filters.ucc.trim())         params.ucc       = filters.ucc.trim();
      if (filters.exchange !== 'ALL') params.exchange  = filters.exchange;
      if (filters.segment  !== 'ALL') params.segment   = filters.segment;
      if (filters.status   !== 'ALL') params.status    = filters.status;
      if (filters.date_from)          params.date_from = filters.date_from;
      if (filters.date_to)            params.date_to   = filters.date_to;
      const res = await getAdminOrders(params);
      setOrders(res.data.orders);
    } catch { showToast('Failed to fetch report.', 'error'); }
    finally { setLoading(false); }
  };

  const handleReset = () => {
    setFilters({ ucc: '', exchange: 'ALL', segment: 'ALL', status: 'ALL', date_from: TODAY, date_to: TODAY });
    setOrders([]); setSearched(false);
  };

  const downloadCSV = () => {
    if (!orders.length) return showToast('No data to download.', 'error');
    const headers = ['UCC','Client Name','Symbol','Exchange','Segment','Expiry Date','Strike Price','Option Type','B/S','Quantity','Order Type','Placed On','Traded On','Trade Price','SQF Status','Placed By','Dealer ID','File Generated'];
    const rows = orders.map(o => [
      o.ucc, o.client_name||'', o.symbol, o.exchange, o.segment,
      o.expiry_date  ? new Date(o.expiry_date).toLocaleDateString('en-IN') : '',
      o.strike_price || '', o.option_type || '', o.side, o.quantity, o.order_type,
      fmtDT(o.placed_at), fmtDT(o.traded_at), o.trade_price||'',
      o.status, o.placed_by||'CLIENT', o.dealer_id||'', o.file_generated?'YES':'NO'
    ]);
    const csv  = [headers,...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `OrderReport_${TODAY}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    showToast(`Report downloaded — ${orders.length} records`);
  };

  const inputStyle = { padding: '7px 12px', borderRadius: 6, border: '0.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#0f172a', outline: 'none', fontFamily: "'Segoe UI', Arial, sans-serif" };
  const COLS = '90px 130px 110px 70px 60px 100px 80px 70px 50px 60px 80px 150px 150px 80px 120px 80px 100px';

  const totalOrders   = orders.length;
  const traded        = orders.filter(o => o.status === 'ORDER_TRADED').length;
  const fileGenerated = orders.filter(o => o.status === 'FILE_GENERATED').length;
  const received      = orders.filter(o => o.status === 'ORDER_RECEIVED').length;

  const statCards = [
    { label: 'Total Orders',   value: totalOrders,   color: '#1d4ed8', bg: '#eff6ff', filter: null },
    { label: 'Order Received', value: received,       color: '#7c3aed', bg: '#fdf4ff', filter: 'ORDER_RECEIVED' },
    { label: 'File Generated', value: fileGenerated,  color: '#c2410c', bg: '#fff7ed', filter: 'FILE_GENERATED' },
    { label: 'Order Traded',   value: traded,         color: '#15803d', bg: '#f0fdf4', filter: 'ORDER_TRADED' },
  ];

  const openModal = (statusFilter) => {
    const filtered = statusFilter ? orders.filter(o => o.status === statusFilter) : orders;
    setModal({ orders: filtered, label: statusFilter ? STATUS_CONFIG[statusFilter]?.label : 'All Orders' });
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {toast.msg}
        </div>
      )}

      {/* Drill-Down Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 1050, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '18px 24px 14px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{modal.label}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{modal.orders.length} records</div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#374151', cursor: 'pointer' }}>✕ Close</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {modal.orders.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No records found.</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 110px 1fr 70px 60px 60px 70px 150px 150px 120px 130px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0', position: 'sticky', top: 0 }}>
                    {['UCC','Client','Symbol','Exch','Seg','Side','Qty','Placed At','Traded At','Placed By','Dealer ID'].map(h => (
                      <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                    ))}
                  </div>
                  {modal.orders.map((o, i) => (
                    <div key={o.order_id} style={{ display: 'grid', gridTemplateColumns: '100px 110px 1fr 70px 60px 60px 70px 150px 150px 120px 130px', padding: '11px 20px', alignItems: 'center', borderBottom: i < modal.orders.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{o.ucc}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{o.client_name || '—'}</div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{o.symbol}</div>
                      <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: EXCHANGE_STYLE[o.exchange]?.bg, color: EXCHANGE_STYLE[o.exchange]?.color, border: `0.5px solid ${EXCHANGE_STYLE[o.exchange]?.border}` }}>{o.exchange}</span></div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{o.segment}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: o.side === 'SELL' ? '#dc2626' : '#15803d' }}>{o.side}</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{o.quantity}</div>
                      <div style={{ fontSize: 11, color: '#374151' }}>{fmtDT(o.placed_at)}</div>
                      <div style={{ fontSize: 11, color: o.traded_at ? '#15803d' : '#94a3b8', fontWeight: o.traded_at ? 500 : 400 }}>{fmtDT(o.traded_at)}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{o.placed_by || 'CLIENT'}</div>
                      <div style={{ fontSize: 11, fontWeight: o.dealer_id ? 600 : 400, color: o.dealer_id ? '#1d4ed8' : '#94a3b8' }}>{o.dealer_id || '—'}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div style={{ padding: '12px 24px', borderTop: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>{modal.orders.length} records</span>
              <button onClick={() => setModal(null)} style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Search Panel */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: '20px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Search Orders</div>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 16 }}>Filter by UCC, exchange, segment, status or date range</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {[
            { label: 'UCC', el: <input type="text" value={filters.ucc} onChange={e => setFilter('ucc', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Enter UCC..." style={{ ...inputStyle, width: 140 }} /> },
            { label: 'Exchange', el: <select value={filters.exchange} onChange={e => setFilter('exchange', e.target.value)} style={inputStyle}>{['ALL','NSE','BSE','MCX'].map(e => <option key={e} value={e}>{e === 'ALL' ? 'All' : e}</option>)}</select> },
            { label: 'Segment',  el: <select value={filters.segment}  onChange={e => setFilter('segment',  e.target.value)} style={inputStyle}>{['ALL','CM','FO'].map(s => <option key={s} value={s}>{s === 'ALL' ? 'All' : s}</option>)}</select> },
            { label: 'Status',   el: <select value={filters.status}   onChange={e => setFilter('status',   e.target.value)} style={inputStyle}><option value="ALL">All</option><option value="ORDER_RECEIVED">Order Received</option><option value="FILE_GENERATED">File Generated</option><option value="ORDER_TRADED">Order Traded</option></select> },
            { label: 'From Date', el: <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} style={inputStyle} /> },
            { label: 'To Date',   el: <input type="date" value={filters.date_to}   onChange={e => setFilter('date_to',   e.target.value)} style={inputStyle} /> },
          ].map(f => (
            <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{f.label}</label>
              {f.el}
            </div>
          ))}
          <button onClick={handleSearch} disabled={loading} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', alignSelf: 'flex-end' }}>
            {loading ? 'Searching...' : '🔍 Search'}
          </button>
          <button onClick={handleReset} style={{ padding: '7px 16px', borderRadius: 6, border: '0.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer', alignSelf: 'flex-end' }}>Reset</button>
          {orders.length > 0 && (
            <button onClick={downloadCSV} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#15803d', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', alignSelf: 'flex-end' }}>↓ Download CSV</button>
          )}
        </div>
      </div>

      {/* Result Stats — Clickable */}
      {searched && !loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
          {statCards.map(c => (
            <div key={c.label}
              onClick={() => openModal(c.filter)}
              onMouseEnter={e => { e.currentTarget.style.borderColor = c.color; e.currentTarget.style.boxShadow = `0 0 0 2px ${c.bg}`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
              style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', cursor: 'pointer', transition: 'all 0.15s' }}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{c.label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: c.color, textDecoration: 'underline', textUnderlineOffset: 3 }}>{c.value}</div>
                <span style={{ fontSize: 10, color: c.color, background: c.bg, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>View →</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results Table */}
      {searched && (
        <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'auto' }}>
          <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
              Report Results {orders.length > 0 && <span style={{ color: '#64748b', fontWeight: 400 }}>— {orders.length} records</span>}
            </div>
            {orders.length > 0 && (
              <button onClick={downloadCSV} style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#64748b', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>↓ Export CSV</button>
            )}
          </div>
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Searching...</div>
          ) : orders.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 14, color: '#64748b' }}>No records found for the selected filters.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '10px 16px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0', minWidth: 1400 }}>
                {['UCC','Client','Symbol','Exch','Seg','Expiry','Strike','Type','B/S','Qty','OrderType','Placed On','Traded On','Price','Status','PlacedBy','Dealer'].map(h => (
                  <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                ))}
              </div>
              {orders.map((o, i) => {
                const st      = STATUS_CONFIG[o.status] || STATUS_CONFIG.ORDER_RECEIVED;
                const exStyle = EXCHANGE_STYLE[o.exchange] || {};
                return (
                  <div key={o.order_id} style={{ display: 'grid', gridTemplateColumns: COLS, padding: '11px 16px', alignItems: 'center', borderBottom: i < orders.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa', minWidth: 1400 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{o.ucc}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{o.client_name || '—'}</div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{o.symbol}</div>
                    <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: exStyle.bg, color: exStyle.color, border: `0.5px solid ${exStyle.border}` }}>{o.exchange}</span></div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{o.segment}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{o.expiry_date ? new Date(o.expiry_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}) : '—'}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{o.strike_price || '—'}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{o.option_type  || '—'}</div>
                    <div><span style={{ fontSize: 11, fontWeight: 600, color: o.side === 'SELL' ? '#dc2626' : '#15803d' }}>{o.side}</span></div>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{o.quantity}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{o.order_type}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{fmtDT(o.placed_at)}</div>
                    <div style={{ fontSize: 10, color: o.traded_at ? '#15803d' : '#94a3b8' }}>{fmtDT(o.traded_at)}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{o.trade_price || '—'}</div>
                    <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{st.label}</span></div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{o.placed_by || 'CLIENT'}</div>
                    <div style={{ fontSize: 11, fontWeight: o.dealer_id ? 600 : 400, color: o.dealer_id ? '#1d4ed8' : '#94a3b8' }}>{o.dealer_id || '—'}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}